import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAddresses, api } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AddressPickerModal from '../../components/common/AddressPickerModal';
import { 
  ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Plus, Check, 
  ChevronRight, CreditCard, Banknote, Car, AlertCircle, Wallet, Info, Tag,
  ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Fallback time slots (used when provider availability is not set)
const defaultTimeSlots = [
  '08:00', '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

export default function BookingFlow() {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const location = useLocation();
  const rebookState = location.state;
  const { addresses, addAddress, isLoading: addressLoading } = useAddresses();
  
  const [step, setStep] = useState(1); // 1: Address, 2: Schedule, 3: Review
  const [service, setService] = useState(null);
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  
  // Top-up dialog state
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpPaymentMethod, setTopUpPaymentMethod] = useState('gcash');
  const [isTopUpLoading, setIsTopUpLoading] = useState(false);
  
  // Available slots state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsMessage, setSlotsMessage] = useState('');
  const [travelBuffer, setTravelBuffer] = useState(null);
  
  // Bulk discount settings
  const [bulkDiscount, setBulkDiscount] = useState({ enabled: false, tiers: [] });
  
  // Insurance settings
  const [insuranceSettings, setInsuranceSettings] = useState(null);
  const [showInsuranceTerms, setShowInsuranceTerms] = useState(false);
  
  // Wallet settings
  const [walletSettings, setWalletSettings] = useState({ min_topup_amount: 100 });
  
  const [bookingData, setBookingData] = useState({
    address_id: '',
    scheduled_date: null,
    scheduled_time: '',
    notes: '',
    add_ons: [],
    auto_assign: true,
    payment_method: 'cash',
    units: 1,  // Number of service units (e.g., 3 units of 30-min service = 1.5 hours)
    include_insurance: false  // Service guarantee insurance
  });

  useEffect(() => {
    const fetchService = async () => {
      try {
        // Get provider ID from URL params if available
        const searchParams = new URLSearchParams(window.location.search);
        const providerId = searchParams.get('provider');
        
        const response = await axios.get(`${API}/services/${serviceId}`);
        const serviceData = response.data;
        
        // If provider_id is in the service data, use that
        if (serviceData.provider_id || providerId) {
          serviceData.selected_provider_id = serviceData.provider_id || providerId;
        }
        
        setService(serviceData);
        
        // Fetch category
        if (serviceData.category_id) {
          try {
            const catResponse = await axios.get(`${API}/categories/${serviceData.category_id}`);
            setCategory(catResponse.data);
          } catch (e) {
            // Category may be in service data already
            if (serviceData.category_name) {
              setCategory({ name: serviceData.category_name, id: serviceData.category_id });
            }
          }
        }
        
        // Fetch wallet balance
        try {
          const walletRes = await api.get('/wallet');
          setWalletBalance(walletRes.data?.balance || 0);
        } catch (e) {
          setWalletBalance(0);
        }
        
        // Fetch bulk discount settings
        try {
          const discountRes = await axios.get(`${API}/bulk-discount/settings`);
          if (discountRes.data) setBulkDiscount(discountRes.data);
        } catch (e) {
          // Bulk discount settings not available - silently ignore
        }
        
        // Fetch insurance settings
        try {
          const insuranceRes = await axios.get(`${API}/insurance/settings`);
          if (insuranceRes.data && insuranceRes.data.enabled && insuranceRes.data.customer_insurance_enabled) {
            setInsuranceSettings(insuranceRes.data);
            // Auto-include insurance if mandatory
            if (insuranceRes.data.customer_insurance_mandatory) {
              setBookingData(prev => ({ ...prev, include_insurance: true }));
            }
          }
        } catch (e) {
          // Insurance settings not available - silently ignore
        }
        
        // Fetch wallet settings
        try {
          const walletSettingsRes = await axios.get(`${API}/wallet-settings`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (walletSettingsRes.data?.min_topup_amount) setWalletSettings(walletSettingsRes.data);
        } catch (e) {
          // Wallet settings not available - use default
        }
      } catch (error) {
        console.error('Failed to fetch service:', error);
        toast.error('Service not found');
        navigate('/home');
      } finally {
        setIsLoading(false);
      }
    };
    fetchService();
  }, [serviceId, navigate]);

  // Fetch available slots when date changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!bookingData.scheduled_date || !service?.provider_id) {
        setAvailableSlots([]);
        return;
      }
      
      setIsLoadingSlots(true);
      setSlotsMessage('');
      setTravelBuffer(null);
      
      try {
        const selectedAddress = addresses.find(a => a.id === bookingData.address_id);
        const dateStr = format(bookingData.scheduled_date, 'yyyy-MM-dd');
        
        // Get service duration
        let duration = 1;
        if (service.estimated_duration) {
          if (typeof service.estimated_duration === 'string') {
            const match = service.estimated_duration.match(/(\d+(?:\.\d+)?)/);
            duration = match ? parseFloat(match[1]) : 1;
          } else {
            duration = service.estimated_duration / 60; // Convert minutes to hours
          }
        }
        
        const params = new URLSearchParams({
          date: dateStr,
          service_duration_hours: duration.toString()
        });
        
        // Add customer location for travel buffer calculation
        if (selectedAddress?.latitude && selectedAddress?.longitude) {
          params.append('customer_lat', selectedAddress.latitude.toString());
          params.append('customer_lng', selectedAddress.longitude.toString());
        }
        
        const response = await axios.get(
          `${API}/providers/${service.provider_id}/available-slots?${params.toString()}`
        );
        
        const data = response.data;
        setAvailableSlots(data.slots || []);
        setSlotsMessage(data.message || '');
        setTravelBuffer(data.travel_buffer_applied);
        
        // Clear selected time if no longer available
        if (data.slots && !data.slots.some(s => s.time === bookingData.scheduled_time)) {
          setBookingData(prev => ({ ...prev, scheduled_time: '' }));
        }
      } catch (error) {
        console.error('Failed to fetch available slots:', error);
        // Fall back to default slots
        setAvailableSlots(defaultTimeSlots.map(t => ({ time: t, formatted: formatTime12h(t) })));
      } finally {
        setIsLoadingSlots(false);
      }
    };
    
    fetchAvailableSlots();
  }, [bookingData.scheduled_date, service?.provider_id, bookingData.address_id, addresses]);

  // Helper to format time to 12h
  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [hour, minute] = time24.split(':').map(Number);
    const period = hour < 12 ? 'AM' : 'PM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const handleAddressSelect = (addressId) => {
    setBookingData(prev => ({ ...prev, address_id: addressId }));
  };

  const handleAddOnToggle = (addOnId) => {
    setBookingData(prev => ({
      ...prev,
      add_ons: prev.add_ons.includes(addOnId)
        ? prev.add_ons.filter(id => id !== addOnId)
        : [...prev.add_ons, addOnId]
    }));
  };

  // Handle saving address from the AddressPickerModal
  const handleSaveAddress = async (addressData) => {
    try {
      const added = await addAddress({
        ...addressData,
        is_default: addresses.length === 0
      });
      setBookingData(prev => ({ ...prev, address_id: added.id }));
      setShowAddressDialog(false);
      toast.success('Address added successfully');
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const calculateTotal = () => {
    if (!service) return { base: 0, addOns: 0, total: 0, units: 1, totalDuration: 0, insuranceFee: 0 };
    
    const units = bookingData.units || 1;
    const basePerUnit = service.base_price;
    const base = basePerUnit * units;
    const addOns = bookingData.add_ons.reduce((sum, addOnId) => {
      const addon = service.add_ons?.find(a => a.id === addOnId);
      return sum + (addon?.price || 0);
    }, 0);
    
    // Calculate total duration in minutes
    let durationPerUnit = 60; // default 1 hour
    if (service.estimated_duration) {
      if (typeof service.estimated_duration === 'string') {
        const match = service.estimated_duration.match(/(\d+(?:\.\d+)?)/);
        durationPerUnit = match ? parseFloat(match[1]) * 60 : 60;
      } else {
        durationPerUnit = service.estimated_duration;
      }
    }
    const totalDuration = durationPerUnit * units;
    
    // Calculate bulk discount
    let discountPercentage = 0;
    let discountAmount = 0;
    let appliedTier = null;
    
    if (bulkDiscount.enabled && bulkDiscount.tiers?.length > 0) {
      // Find the highest applicable discount tier
      const sortedTiers = [...bulkDiscount.tiers].sort((a, b) => b.min_units - a.min_units);
      for (const tier of sortedTiers) {
        if (units >= tier.min_units) {
          discountPercentage = tier.discount_percentage;
          appliedTier = tier.min_units;
          break;
        }
      }
      discountAmount = Math.round((base + addOns) * (discountPercentage / 100));
    }
    
    const subtotal = base + addOns - discountAmount;
    
    // Calculate insurance fee
    let insuranceFee = 0;
    if (bookingData.include_insurance && insuranceSettings) {
      if (insuranceSettings.customer_pricing_type === 'flat') {
        insuranceFee = insuranceSettings.customer_flat_fee || 0;
      } else {
        insuranceFee = Math.round(subtotal * (insuranceSettings.customer_percentage || 0) / 100);
      }
    }
    
    const total = subtotal + insuranceFee;
    
    return { 
      base, 
      addOns, 
      subtotal: base + addOns,
      discountPercentage, 
      discountAmount, 
      appliedTier,
      insuranceFee,
      total, 
      units, 
      basePerUnit, 
      totalDuration 
    };
  };

  const handleSubmit = async () => {
    if (!bookingData.address_id) {
      toast.error('Please select an address');
      return;
    }
    if (!bookingData.scheduled_date || !bookingData.scheduled_time) {
      toast.error('Please select date and time');
      return;
    }
    
    // Check wallet balance for wallet payment
    if (bookingData.payment_method === 'wallet' && walletBalance < total) {
      toast.error('Insufficient wallet balance. Please top up or use cash payment.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.createBooking({
        service_id: serviceId,
        category_id: service.category_id,
        address_id: bookingData.address_id,
        scheduled_date: format(bookingData.scheduled_date, 'yyyy-MM-dd'),
        scheduled_time: bookingData.scheduled_time,
        notes: bookingData.notes,
        add_ons: bookingData.add_ons,
        auto_assign: bookingData.auto_assign,
        payment_method: bookingData.payment_method,
        units: bookingData.units || 1,  // Send units to backend
        include_insurance: bookingData.include_insurance  // Send insurance selection
      });
      
      toast.success('Booking created successfully!');
      navigate(`/booking/${response.data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle identity verification redirect
      if (detail && typeof detail === 'object' && detail.action === 'verify_identity') {
        toast.error(detail.message);
        navigate('/verification');
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed to create booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle top-up from booking flow
  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    const minAmount = walletSettings.min_topup_amount || 100;
    if (!amount || amount < minAmount) {
      toast.error(`Minimum top-up amount is ₱${minAmount}`);
      return;
    }
    if (amount > 50000) {
      toast.error('Maximum top-up amount is ₱50,000');
      return;
    }
    
    setIsTopUpLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/wallet/topup`, {
        amount: amount,
        payment_method: topUpPaymentMethod,
        return_url: window.location.href
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If PayMongo mode, redirect through payment page to prevent navigation issues
      if (response.data?.checkout_url && response.data?.mode === 'paymongo') {
        // Store booking state before redirecting
        sessionStorage.setItem('pendingBooking', JSON.stringify({
          serviceId,
          bookingData,
          returnUrl: window.location.href
        }));
        
        const transactionId = response.data.transaction_id || '';
        const checkoutUrl = encodeURIComponent(response.data.checkout_url);
        const returnUrl = encodeURIComponent(window.location.pathname);
        navigate(`/payment?checkout_url=${checkoutUrl}&transaction_id=${transactionId}&return_url=${returnUrl}&user_type=customer`);
        return;
      }
      
      // Mock mode: instant credit
      toast.success(`₱${amount} added to wallet!`);
      setShowTopUpDialog(false);
      setTopUpAmount('');
      refreshWalletBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate top-up');
    } finally {
      setIsTopUpLoading(false);
    }
  };

  // Refresh wallet balance (called after returning from payment)
  const refreshWalletBalance = async () => {
    try {
      const walletRes = await api.get('/wallet');
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (e) {
      console.error('Failed to refresh wallet balance:', e);
    }
  };

  // Check for payment return and refresh wallet
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_return') === 'true') {
      // Refresh wallet balance after returning from payment
      refreshWalletBalance();
      toast.success('Payment completed! Your wallet has been updated.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?provider=' + (new URLSearchParams(window.location.search).get('provider') || ''));
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const { base, addOns, total } = calculateTotal();
  const selectedAddress = addresses.find(a => a.id === bookingData.address_id);

  return (
    <div className="min-h-screen bg-stone-50 pb-32">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="font-bold text-slate-900 font-[Poppins]">Book Service</h1>
            <p className="text-sm text-slate-500">{service?.name}</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mt-4">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-slate-900' : 'bg-stone-200'}`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-500">
          <span className={step >= 1 ? 'text-slate-900 font-medium' : ''}>Location</span>
          <span className={step >= 2 ? 'text-slate-900 font-medium' : ''}>Schedule</span>
          <span className={step >= 3 ? 'text-slate-900 font-medium' : ''}>Confirm</span>
        </div>
      </header>

      <main className="px-4 py-6">
        {/* Step 1: Address Selection */}
        {step === 1 && (
          <div className="animate-in">
            <h2 className="text-lg font-bold text-slate-900 mb-4 font-[Poppins]">Where do you need the service?</h2>
            
            <div className="space-y-3 mb-4">
              {addresses.map((address) => (
                <button
                  key={address.id}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                    bookingData.address_id === address.id 
                      ? 'border-slate-900 bg-slate-50' 
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                  onClick={() => handleAddressSelect(address.id)}
                  data-testid={`address-${address.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        bookingData.address_id === address.id ? 'bg-slate-900' : 'bg-stone-100'
                      }`}>
                        <MapPin className={`w-5 h-5 ${
                          bookingData.address_id === address.id ? 'text-white' : 'text-slate-500'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{address.label}</p>
                        <p className="text-sm text-slate-500 mt-0.5">{address.full_address}</p>
                        <p className="text-sm text-slate-400">{address.city}</p>
                      </div>
                    </div>
                    {bookingData.address_id === address.id && (
                      <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
              
              {addresses.length === 0 && (
                <p className="text-center text-slate-500 py-4">No addresses saved yet</p>
              )}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full h-12"
              onClick={() => setShowAddressDialog(true)}
              data-testid="add-address-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Address
            </Button>
          </div>
        )}

        {/* Step 2: Schedule */}
        {step === 2 && (
          <div className="animate-in">
            <h2 className="text-lg font-bold text-slate-900 mb-4 font-[Poppins]">When do you need it?</h2>
            
            {/* Date Picker */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-12"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {bookingData.scheduled_date ? (
                      format(bookingData.scheduled_date, 'PPP')
                    ) : (
                      <span className="text-slate-500">Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={bookingData.scheduled_date}
                    onSelect={(date) => setBookingData(prev => ({ ...prev, scheduled_date: date, scheduled_time: '' }))}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Available Time Slots */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium text-slate-700">Available Time Slots</Label>
                {travelBuffer && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Car className="w-3 h-3" />
                    ~{travelBuffer} min travel
                  </span>
                )}
              </div>
              
              {!bookingData.scheduled_date ? (
                <div className="text-center py-6 text-slate-400">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select a date first</p>
                </div>
              ) : isLoadingSlots ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
                  <span className="ml-2 text-sm text-slate-500">Loading available slots...</span>
                </div>
              ) : slotsMessage ? (
                <div className="text-center py-6">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                  <p className="text-sm text-slate-600">{slotsMessage}</p>
                  <p className="text-xs text-slate-400 mt-1">Try selecting a different date</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No slots available on this date</p>
                  <p className="text-xs mt-1">The provider may be fully booked</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.time}
                      className={`py-2.5 px-2 rounded-lg text-sm font-medium transition-colors ${
                        bookingData.scheduled_time === slot.time
                          ? 'bg-slate-900 text-white'
                          : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                      }`}
                      onClick={() => setBookingData(prev => ({ ...prev, scheduled_time: slot.time }))}
                      data-testid={`time-${slot.time}`}
                    >
                      {slot.formatted || slot.time}
                    </button>
                  ))}
                </div>
              )}
              
              {availableSlots.length > 0 && (
                <p className="text-xs text-slate-400 mt-3 text-center">
                  {availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available
                </p>
              )}
            </div>
            
            {/* Add-ons */}
            {service?.add_ons && service.add_ons.length > 0 && (
              <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Add-ons (Optional)</Label>
                <div className="space-y-2">
                  {service.add_ons.map((addon) => (
                    <label 
                      key={addon.id}
                      className="flex items-center justify-between p-3 rounded-xl border border-stone-200 cursor-pointer hover:bg-stone-50"
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={bookingData.add_ons.includes(addon.id)}
                          onCheckedChange={() => handleAddOnToggle(addon.id)}
                        />
                        <span className="text-sm text-slate-700">{addon.name}</span>
                      </div>
                      <span className="text-sm font-medium text-amber-600">+₱{addon.price}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Service Units - Multiple Bookings */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-slate-700 block">Number of Units</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Each unit = {service?.estimated_duration ? 
                      (typeof service.estimated_duration === 'string' ? service.estimated_duration : `${Math.round(service.estimated_duration)} mins`) 
                      : '1 hour'} at ₱{service?.base_price?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setBookingData(prev => ({ ...prev, units: Math.max(1, (prev.units || 1) - 1) }))}
                    disabled={bookingData.units <= 1}
                    className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-slate-700 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="units-decrease-btn"
                  >
                    <span className="text-xl font-medium">−</span>
                  </button>
                  <span className="w-8 text-center text-lg font-bold text-slate-900" data-testid="units-count">
                    {bookingData.units || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setBookingData(prev => ({ ...prev, units: Math.min(10, (prev.units || 1) + 1) }))}
                    disabled={bookingData.units >= 10}
                    className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="units-increase-btn"
                  >
                    <span className="text-xl font-medium">+</span>
                  </button>
                </div>
              </div>
              
              {/* Bulk Discount Info Tooltip */}
              {bulkDiscount.enabled && bulkDiscount.tiers?.length > 0 && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="bulk-discount-info">
                  <div className="flex items-start gap-2">
                    <Tag className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Bulk Booking Discounts Available!</p>
                      <ul className="text-xs text-green-700 mt-1 space-y-0.5">
                        {bulkDiscount.tiers
                          .sort((a, b) => a.min_units - b.min_units)
                          .map((tier) => (
                            <li key={`tier-${tier.min_units}`} className={bookingData.units >= tier.min_units ? 'font-semibold' : ''}>
                              • {tier.min_units}+ units: <strong>{tier.discount_percentage}% off</strong>
                              {bookingData.units >= tier.min_units && ' ✓'}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {bookingData.units > 1 && (
                <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Total Duration:</strong> {Math.round(calculateTotal().totalDuration)} mins ({(calculateTotal().totalDuration / 60).toFixed(1)} hrs)
                    <br />
                    <strong>Total Service Cost:</strong> ₱{calculateTotal().base.toLocaleString()} ({bookingData.units} × ₱{service?.base_price?.toLocaleString()})
                    {calculateTotal().discountAmount > 0 && (
                      <>
                        <br />
                        <span className="text-green-700">
                          <strong>Bulk Discount ({calculateTotal().discountPercentage}%):</strong> -₱{calculateTotal().discountAmount.toLocaleString()}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <Label htmlFor="notes" className="text-sm font-medium text-slate-700 mb-2 block">
                Special Instructions (Optional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Any specific details for the provider..."
                value={bookingData.notes}
                onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
                className="min-h-[80px]"
                data-testid="notes-input"
              />
            </div>
          </div>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <div className="animate-in">
            <h2 className="text-lg font-bold text-slate-900 mb-4 font-[Poppins]">Review Your Booking</h2>
            
            {/* Service Summary */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Service</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{service?.name}</p>
                  <p className="text-sm text-slate-500">{category?.name}</p>
                </div>
              </div>
            </div>
            
            {/* Location */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Location</h3>
              {selectedAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">{selectedAddress.label}</p>
                    <p className="text-sm text-slate-500">{selectedAddress.full_address}</p>
                    <p className="text-sm text-slate-400">{selectedAddress.city}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Schedule */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Schedule</h3>
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">
                    {bookingData.scheduled_date && format(bookingData.scheduled_date, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm text-slate-500">{bookingData.scheduled_time}</p>
                </div>
              </div>
            </div>
            
            {/* Payment Method */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Payment Method</h3>
              <div className="space-y-2">
                <label className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer ${
                  bookingData.payment_method === 'cash' ? 'border-slate-900 bg-slate-50' : 'border-stone-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-slate-900">Cash</span>
                  </div>
                  <input 
                    type="radio" 
                    name="payment" 
                    checked={bookingData.payment_method === 'cash'}
                    onChange={() => setBookingData(prev => ({ ...prev, payment_method: 'cash' }))}
                    className="hidden"
                  />
                  {bookingData.payment_method === 'cash' && (
                    <Check className="w-5 h-5 text-slate-900" />
                  )}
                </label>
                <label className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer ${
                  bookingData.payment_method === 'wallet' ? 'border-slate-900 bg-slate-50' : 'border-stone-200'
                } ${walletBalance < total ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <div>
                      <span className="font-medium text-slate-900">Wallet</span>
                      <p className={`text-xs ${walletBalance >= total ? 'text-green-600' : 'text-red-500'}`}>
                        Balance: ₱{walletBalance.toLocaleString()}
                        {walletBalance < total && ' (Insufficient)'}
                      </p>
                    </div>
                  </div>
                  <input 
                    type="radio" 
                    name="payment" 
                    checked={bookingData.payment_method === 'wallet'}
                    onChange={() => walletBalance >= total && setBookingData(prev => ({ ...prev, payment_method: 'wallet' }))}
                    disabled={walletBalance < total}
                    className="hidden"
                  />
                  {bookingData.payment_method === 'wallet' && (
                    <Check className="w-5 h-5 text-slate-900" />
                  )}
                </label>
                
                {/* Top Up Button - shown when wallet balance is insufficient */}
                {walletBalance < total && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="text-sm text-amber-800">
                          Need ₱{(total - walletBalance).toLocaleString()} more
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setShowTopUpDialog(true)}
                        className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                        data-testid="topup-btn"
                      >
                        <Wallet className="w-4 h-4 mr-1" />
                        Top Up
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Insurance Option */}
            {insuranceSettings && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Service Guarantee Insurance
                  {insuranceSettings.customer_insurance_mandatory && (
                    <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-medium" data-testid="insurance-mandatory-badge">Required</span>
                  )}
                </h3>
                <label className={`block p-3 border-2 rounded-xl transition-all ${
                  insuranceSettings.customer_insurance_mandatory 
                    ? 'border-blue-500 bg-blue-50 cursor-default' 
                    : bookingData.include_insurance 
                      ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                      : 'border-stone-200 hover:border-stone-300 cursor-pointer'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={bookingData.include_insurance}
                      onCheckedChange={(checked) => {
                        if (!insuranceSettings.customer_insurance_mandatory) {
                          setBookingData(prev => ({ ...prev, include_insurance: checked }));
                        }
                      }}
                      disabled={insuranceSettings.customer_insurance_mandatory}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">Add Insurance Protection</span>
                        <span className="font-semibold text-blue-600">
                          {insuranceSettings.customer_pricing_type === 'flat' 
                            ? `₱${insuranceSettings.customer_flat_fee}` 
                            : `${insuranceSettings.customer_percentage}%`
                          }
                        </span>
                      </div>
                      
                      {/* Coverage badges */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {insuranceSettings.customer_covers_property_damage && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Property Damage</span>
                        )}
                        {insuranceSettings.customer_covers_incomplete_service && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Incomplete Service</span>
                        )}
                        {insuranceSettings.customer_covers_no_show && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">No-Show</span>
                        )}
                      </div>
                      
                      <p className="text-xs text-slate-400 mt-2">
                        Coverage limit: ₱{insuranceSettings.customer_coverage_limit?.toLocaleString() || '10,000'}
                      </p>
                      
                      {/* View Terms & Conditions - Opens Popup */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowInsuranceTerms(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline mt-2 font-medium"
                      >
                        View Terms & Conditions
                      </button>
                    </div>
                  </div>
                </label>
              </div>
            )}
            
            {/* Price Breakdown */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3">Price Summary</h3>
              <div className="space-y-2">
                {bookingData.units > 1 ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Price per unit</span>
                      <span className="text-slate-900">₱{service?.base_price?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Units × {bookingData.units}</span>
                      <span className="text-slate-900">₱{base.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm text-amber-700 bg-amber-50 -mx-4 px-4 py-1">
                      <span>Total Duration</span>
                      <span>{Math.round(calculateTotal().totalDuration)} mins</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Base Price</span>
                    <span className="text-slate-900">₱{base.toLocaleString()}</span>
                  </div>
                )}
                {addOns > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Add-ons</span>
                    <span className="text-slate-900">₱{addOns.toLocaleString()}</span>
                  </div>
                )}
                {calculateTotal().discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-700 bg-green-50 -mx-4 px-4 py-1">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      Bulk Discount ({calculateTotal().discountPercentage}%)
                    </span>
                    <span>-₱{calculateTotal().discountAmount.toLocaleString()}</span>
                  </div>
                )}
                {calculateTotal().insuranceFee > 0 && (
                  <div className="flex justify-between text-sm text-blue-700 bg-blue-50 -mx-4 px-4 py-1">
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Insurance
                    </span>
                    <span>+₱{calculateTotal().insuranceFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-stone-200 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-900">Total</span>
                    <span className="font-bold text-xl text-amber-600">₱{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-4 safe-bottom">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-900">₱{total}</p>
          </div>
          <Button 
            size="lg"
            className="bg-slate-900 hover:bg-slate-800 h-14 px-8"
            onClick={() => step < 3 ? setStep(step + 1) : handleSubmit()}
            disabled={
              (step === 1 && !bookingData.address_id) ||
              (step === 2 && (!bookingData.scheduled_date || !bookingData.scheduled_time)) ||
              isSubmitting
            }
            data-testid="continue-btn"
          >
            {isSubmitting ? 'Booking...' : step < 3 ? 'Continue' : 'Confirm Booking'}
            {!isSubmitting && <ChevronRight className="w-5 h-5 ml-2" />}
          </Button>
        </div>
      </div>

      {/* Add Address Modal - Using the same component as Profile */}
      <AddressPickerModal
        open={showAddressDialog}
        onClose={() => setShowAddressDialog(false)}
        onSave={handleSaveAddress}
      />

      {/* Top Up Dialog */}
      <Dialog open={showTopUpDialog} onOpenChange={setShowTopUpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-500" />
              Add Money to Wallet
            </DialogTitle>
            <DialogDescription>
              Add funds to complete your booking with wallet payment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Balance */}
            <div className="p-4 bg-slate-100 rounded-xl text-center">
              <p className="text-sm text-slate-500">Current Balance</p>
              <p className="text-2xl font-bold text-slate-900">₱{walletBalance.toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1">
                Need ₱{total.toLocaleString()} for this booking
              </p>
            </div>
            
            {/* Amount Input */}
            <div>
              <Label>Amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₱</span>
                <Input
                  type="number"
                  placeholder="0"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="h-14 pl-8 text-2xl font-bold"
                  data-testid="topup-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Min: ₱100, Max: ₱50,000</p>
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="flex gap-2">
              {[100, 500, 1000, 2000].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                    topUpAmount === amt.toString()
                      ? 'bg-slate-900 text-white'
                      : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                  }`}
                  onClick={() => setTopUpAmount(amt.toString())}
                >
                  ₱{amt}
                </button>
              ))}
            </div>
            
            {/* Payment Method Selection */}
            <div>
              <Label>Payment Method</Label>
              <div className="space-y-2 mt-2">
                {[
                  { id: 'gcash', name: 'GCash', icon: Wallet },
                  { id: 'grab_pay', name: 'GrabPay', icon: Wallet },
                  { id: 'paymaya', name: 'Maya', icon: Wallet },
                  { id: 'card', name: 'Credit/Debit Card', icon: CreditCard }
                ].map(method => (
                  <label
                    key={method.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${
                      topUpPaymentMethod === method.id
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="topup_payment_method"
                      value={method.id}
                      checked={topUpPaymentMethod === method.id}
                      onChange={(e) => setTopUpPaymentMethod(e.target.value)}
                      className="hidden"
                    />
                    <method.icon className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">{method.name}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* New Balance Preview */}
            {topUpAmount && parseFloat(topUpAmount) > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-700">
                  New balance after top-up: <span className="font-bold">₱{(walletBalance + parseFloat(topUpAmount || 0)).toLocaleString()}</span>
                </p>
                {(walletBalance + parseFloat(topUpAmount || 0)) >= total && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Sufficient for this booking
                  </p>
                )}
              </div>
            )}
            
            <Button 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              onClick={handleTopUp}
              disabled={isTopUpLoading || !topUpAmount || parseFloat(topUpAmount) < (walletSettings.min_topup_amount || 100)}
              data-testid="confirm-topup-btn"
            >
              {isTopUpLoading ? 'Processing...' : 'Proceed to Payment'}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              You will be redirected to complete payment via GCash, Maya, or Card
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Terms & Conditions Popup */}
      <Dialog open={showInsuranceTerms} onOpenChange={setShowInsuranceTerms}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="break-words">Service Guarantee Insurance</span>
            </DialogTitle>
            <DialogDescription>
              Terms and Conditions
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {insuranceSettings && (
              <>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">Coverage Details:</p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    {insuranceSettings.customer_covers_property_damage && (
                      <li>• Property Damage Protection</li>
                    )}
                    {insuranceSettings.customer_covers_incomplete_service && (
                      <li>• Incomplete Service Coverage</li>
                    )}
                    {insuranceSettings.customer_covers_no_show && (
                      <li>• Provider No-Show Protection</li>
                    )}
                    <li>• Coverage Limit: ₱{insuranceSettings.customer_coverage_limit?.toLocaleString() || '10,000'}</li>
                  </ul>
                </div>
                <div 
                  className="prose prose-sm max-w-none insurance-terms-content"
                  dangerouslySetInnerHTML={{ 
                    __html: insuranceSettings.customer_terms || 'This insurance covers property damage, incomplete service, and provider no-show situations. Claims must be filed within 24 hours of service completion.' 
                  }}
                />
                <style>{`
                  .insurance-terms-content {
                    font-size: 13px;
                    line-height: 1.6;
                    color: #374151;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    word-break: normal;
                    white-space: normal;
                    max-width: 100%;
                    width: 100%;
                  }
                  .insurance-terms-content * {
                    max-width: 100%;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: normal;
                    overflow-wrap: break-word;
                  }
                  .insurance-terms-content h1, 
                  .insurance-terms-content h2, 
                  .insurance-terms-content h3 {
                    font-size: 15px;
                    font-weight: 600;
                    margin: 12px 0 6px 0;
                    color: #1e293b;
                  }
                  .insurance-terms-content p {
                    margin: 8px 0;
                  }
                  .insurance-terms-content ul, 
                  .insurance-terms-content ol {
                    margin: 8px 0;
                    padding-left: 20px;
                  }
                  .insurance-terms-content li {
                    margin: 4px 0;
                  }
                `}</style>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
